
import { useState, useEffect } from 'react';
import { getFirstParagraphText, truncateForSeo } from './utils';
import { SEO_DESCRIPTION_MAX_LENGTH } from './constants';

interface UseSeoFieldsProps {
  title: string;
  editorData: any;
  initialSeoTitle?: string;
  initialSeoDescription?: string;
}

export const useSeoFields = ({
  title,
  editorData,
  initialSeoTitle = '',
  initialSeoDescription = ''
}: UseSeoFieldsProps) => {
  // Track if SEO fields have been manually edited by the user
  const [seoTitleEdited, setSeoTitleEdited] = useState(false);
  const [seoDescriptionEdited, setSeoDescriptionEdited] = useState(false);
  const [seoTitle, setSeoTitle] = useState(initialSeoTitle || '');
  const [seoDescription, setSeoDescription] = useState(initialSeoDescription || '');

  // Update SEO title based on article title if not manually edited
  useEffect(() => {
    if (!seoTitleEdited && title) {
      setSeoTitle(title);
    }
  }, [title, seoTitleEdited]);

  // Update SEO description when editor blocks change
  useEffect(() => {
    if (!seoDescriptionEdited && editorData && editorData.blocks) {
      const firstParagraph = getFirstParagraphText(editorData.blocks);
      if (firstParagraph) {
        // Limit to approximately 160 characters (standard SEO length)
        setSeoDescription(truncateForSeo(firstParagraph, SEO_DESCRIPTION_MAX_LENGTH));
      }
    }
  }, [editorData, seoDescriptionEdited]);

  // Set initial edited states based on whether values match defaults
  useEffect(() => {
    if (initialSeoTitle && initialSeoTitle !== title) {
      setSeoTitleEdited(true);
    }
    
    if (initialSeoDescription) {
      setSeoDescriptionEdited(true);
    }
  }, [initialSeoTitle, initialSeoDescription, title]);

  const updateSeoTitle = (value: string) => {
    setSeoTitleEdited(true);
    setSeoTitle(value);
  };

  const updateSeoDescription = (value: string) => {
    setSeoDescriptionEdited(true);
    setSeoDescription(value);
  };

  return {
    seoTitle,
    seoDescription,
    seoTitleEdited,
    seoDescriptionEdited,
    updateSeoTitle,
    updateSeoDescription
  };
};
